import React, { useState } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import { formService } from '../../services/formService';

const ConsultationBooking = () => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const timeSlots = [
    { id: 1, time: '9:00 AM', available: true },
    { id: 2, time: '11:00 AM', available: true },
    { id: 3, time: '2:00 PM', available: false },
    { id: 4, time: '4:00 PM', available: true }
  ];

  const serviceOptions = [
    { value: 'it-engineering', label: 'IT Engineering Consultation' },
    { value: 'entrepreneurship', label: 'Entrepreneurship Guidance' },
    { value: 'creative-direction', label: 'Creative Direction' },
    { value: 'general', label: 'General Inquiry' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Valid email is required';
    }

    if (!formData?.service) {
      newErrors.service = 'Please select a service';
    }

    if (!selectedSlot) {
      newErrors.slot = 'Please select a time slot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await formService?.submitConsultationForm({
        ...formData,
        selectedSlot: timeSlots?.find(slot => slot?.id === selectedSlot)?.time
      });
      
      setSuccess(true);
      setFormData({ name: '', email: '', service: '', message: '' });
      setSelectedSlot(null);

      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to book consultation. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="consultation-widget">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
            <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
          </div>
          <h3 className="text-xl font-semibold mb-2">Consultation Booked!</h3>
          <p className="text-muted-foreground mb-4">
            You'll receive a confirmation email shortly with meeting details.
          </p>
          <Button
            variant="outline"
            onClick={() => setSuccess(false)}
            iconName="Calendar"
            iconPosition="left"
          >
            Book Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="consultation-widget">
      <div className="consultation-header">
        <div className="consultation-icon">
          <Icon name="Calendar" size={24} color="white" />
        </div>
        <div>
          <h3 className="consultation-title">Book a Consultation</h3>
          <p className="text-sm text-muted-foreground">
            Schedule a 30-minute session
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Select Time Slot</label>
          <div className="consultation-slots">
            {timeSlots?.map((slot) => (
              <button
                key={slot?.id}
                type="button"
                className={`consultation-slot ${selectedSlot === slot?.id ? 'selected' : ''} ${!slot?.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => slot?.available && setSelectedSlot(slot?.id)}
                disabled={!slot?.available}
              >
                <div className="font-medium">{slot?.time}</div>
                <div className="text-xs text-muted-foreground">
                  {slot?.available ? 'Available' : 'Booked'}
                </div>
              </button>
            ))}
          </div>
          {errors?.slot && (
            <p className="text-sm text-error mt-1">{errors?.slot}</p>
          )}
        </div>

        <Input
          label="Full Name"
          type="text"
          placeholder="Enter your name"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="your.email@example.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <Select
          label="Service Type"
          placeholder="Select a service"
          options={serviceOptions}
          value={formData?.service}
          onChange={(value) => handleInputChange('service', value)}
          error={errors?.service}
          required
        />

        <Input
          label="Message (Optional)"
          type="text"
          placeholder="Brief description of your needs"
          value={formData?.message}
          onChange={(e) => handleInputChange('message', e?.target?.value)}
        />

        <Button
          type="submit"
          variant="default"
          loading={loading}
          fullWidth
          iconName="Send"
          iconPosition="right"
        >
          Confirm Booking
        </Button>
      </form>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Clock" size={14} color="currentColor" />
          <span>All times shown in your local timezone</span>
        </div>
      </div>
    </div>
  );
};

export default ConsultationBooking;